
import React from "react";
import {
  Typography, Button, Container, Box, Grid, Card,
  CardContent, List, ListItem, ListItemIcon, ListItemText,
  useMediaQuery, useTheme
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Footer from "../../Common/Footer";

import screenimage from "../../Logos/screenimage.png";
import ParentTrust from "../../Logos/Elder&childcarePage/ParentsTrust.png";
import mainimage from "../../Logos/SocialWellness/TopBanner.png";
import groupgatherings from "../../Logos/SocialWellness/groupgatherings.png";
import yoga from "../../Logos/SocialWellness/yoga.png";
import games from "../../Logos/SocialWellness/games.png";
import storytelling from "../../Logos/SocialWellness/storytelling.png";
import dietplans from "../../Logos/SocialWellness/dietplans.png";
import ForSeniors from "../../Logos/SocialWellness/ForSeniors.png";
import workshops from "../../Logos/SocialWellness/workshops.png";
import storyhours from "../../Logos/SocialWellness/storyhours.png";
import mindfullplay from "../../Logos/SocialWellness/mindfullplay.png";
import miniyoga from "../../Logos/SocialWellness/miniyoga.png";
import childfriendly from "../../Logos/SocialWellness/childfriendly.png";
import ForChildren from "../../Logos/SocialWellness/ForChildren.png";

const SocialWellness = () => {
  return (
    <Box sx={{ overflowX: "hidden" }}>
      <HeroSection />
      <OverViewSection />
      <WhatWeOfferSection />
      <ScreenImageSection />
      <ForChildrenSection />
      <ScreenImageSection />
      <SocialWellnessTrust />
      <Footer />
    </Box>
  );
};

// ─── HERO SECTION ─────────────────────────────────────────────────────────────
function HeroSection() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: { xs: "260px", sm: "310px", md: "350px" },
        backgroundImage: `url(${mainimage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        p: { xs: 2, sm: 3, md: 4 },
        color: "#fff",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: "rgba(0,0,0,0.4)",
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          ml: { xs: 1, sm: 3, md: 6 },
          mt: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Typography
          variant="body1"
          sx={{
            mb: 3,
            fontSize: { xs: "26px", sm: "44px", md: "60px", lg: "72px" },
            fontFamily: '"Montserrat", sans-serif',
            lineHeight: "1.2",
            fontWeight: 600,
          }}
        >
          Personalized<br />
          In-Home Care for<br />
          Every Stage of Life
        </Typography>
      </Box>
    </Box>
  );
}

// ─── OVERVIEW SECTION ─────────────────────────────────────────────────────────
function OverViewSection() {
  return (
    <Box
      sx={{
        minHeight: { xs: "auto", md: "30vh" },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, md: 2 },
        bgcolor: "#fff",
      }}
    >
      <Container>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            textAlign: "center",
            color: "#000000",
            fontFamily: "Montserrat Alternates, sans-serif",
            fontWeight: "bold",
            fontSize: { xs: "22px", sm: "26px", md: "32px" },
          }}
        >
          Overview
        </Typography>

        <Typography
          variant="body1"
          sx={{
            maxWidth: 1000,
            fontSize: { xs: "14px", sm: "16px", md: "1.3em" },
            color: "#0161AB",
            textAlign: "center",
            mx: "auto",
            lineHeight: 1.7,
          }}
        >
          At&nbsp;
          <Box component="span" sx={{ fontSize: { xs: "1.3rem", md: "1.8rem" }, fontWeight: "bold" }}>
            Ayuja
          </Box>{" "}
          we believe true health goes beyond physical care — it includes
          emotional resilience, social connection, and a sense of purpose. Our&nbsp;
          <Box component="span" sx={{ fontSize: { xs: "1rem", md: "1.3rem" }, fontWeight: "bold" }}>
            Social & Mental Wellness Activities
          </Box>{" "}
          are thoughtfully designed to uplift the spirits of seniors, children,
          and families, promoting holistic well-being in a supportive community environment.
        </Typography>
      </Container>
    </Box>
  );
}

// ─── SHARED CARE CARD ─────────────────────────────────────────────────────────
function CareCard({ service }) {
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: "#B3FFC4",
        border: "1.5px solid #B3FFC4",
        borderRadius: 2,
        p: { xs: 1.5, md: 2 },
        width: "100%",
        maxWidth: 320,
        minHeight: { xs: "auto", md: "18vh" },
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: "0 !important" }}>
        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <Box
            sx={{
              width: { xs: 52, md: 62 },
              height: { xs: 52, md: 62 },
              backgroundColor: "#22577A",
              borderRadius: "50%",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
              flexShrink: 0,
            }}
          >
            <img
              src={service.image}
              alt={service.title}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontFamily: '"Montserrat", sans-serif',
              color: "#000000",
              flex: 1,
              wordBreak: "break-word",
              fontSize: { xs: "16px", sm: "18px", md: "20px" },
              lineHeight: 1.3,
            }}
          >
            {service.title}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            color: "#1a1a1a",
            flexGrow: 1,
            wordBreak: "break-word",
            fontSize: { xs: "15px", sm: "16px", md: "16px" },
            lineHeight: 1.7,
            px: { xs: 0.5, md: 1 },
          }}
        >
          {service.desc}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ─── FOR SENIORS SECTION ──────────────────────────────────────────────────────
const seniorServices = [
  { title: "Group Gatherings & Celebrations", desc: "Birthday parties, festival events, and social bonding activities.", image: groupgatherings },
  { title: "Meditation & Gentle Yoga", desc: "Guided practices and light movement to reduce stress and enhance well-being.", image: yoga },
  { title: "Memory & Cognitive Games", desc: "Activities designed to improve focus, recall, and mental agility.", image: games },
  { title: "Storytelling & Sharing Circles", desc: "Safe spaces for emotional expression, nostalgia, and connection.", image: storytelling },
  { title: "Personalized Diet & Nutrition Plans", desc: "Age-appropriate meal planning for health management and vitality.", image: dietplans },
];

function WhatWeOfferSection() {
  return (
    <Box sx={{ backgroundColor: "#006D77", py: 0, mb: 0, position: "relative", overflow: "hidden" }}>
      {/* Decorative image — only on large screens */}
      <Box
        component="img"
        src={ForSeniors}
        alt="For Seniors"
        sx={{
          display: { xs: "none", lg: "block" },
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "400px",
          height: "auto",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ py: 3, position: "relative", zIndex: 1 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: "bold",
            fontFamily: '"Montserrat", sans-serif',
            color: "#B3FFC4",
            fontSize: { xs: "22px", sm: "26px", md: "30px" },
          }}
        >
          What We Offer
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 3, color: "#B3FFC4", lineHeight: 1.6,
            textAlign: "center", fontSize: { xs: "13px", md: "16px" },
          }}
        >
          For Seniors
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} justifyContent="center">
          {seniorServices.map((service, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx} sx={{ display: "flex", justifyContent: "center" }}>
              <CareCard service={service} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ─── SCREEN DIVIDER ───────────────────────────────────────────────────────────
function ScreenImageSection() {
  return (
    <Box
      component="img"
      src={screenimage}
      alt="divider"
      sx={{
        width: "100%",
        height: { xs: "6vh", md: "10vh" },
        objectFit: "cover",
        borderRadius: "0px 0px 100px 0px",
        display: "block",
      }}
    />
  );
}

// ─── FOR CHILDREN SECTION ─────────────────────────────────────────────────────
const childServices = [
  { title: "Creative Workshops", desc: "Engaging sessions in art, music, and crafts to foster imagination and expression.", image: workshops },
  { title: "Group Playdates & Story Hours", desc: "Safe, supervised fun that encourages friendship and social development.", image: storyhours },
  { title: "Mindful Play & Emotion Coaching", desc: "Helping kids understand and manage emotions through structured activities.", image: mindfullplay },
  { title: "Movement & Mini-Yoga Sessions", desc: "Child-friendly physical activities for body awareness and calmness.", image: miniyoga },
  { title: "Child-Friendly Nutrition Guidance", desc: "Balanced meal suggestions tailored to age, activity levels, and preferences.", image: childfriendly },
];

function ForChildrenSection() {
  return (
    <Box sx={{ backgroundColor: "#006D77", py: 0, mb: 0, position: "relative", overflow: "hidden" }}>
      {/* Decorative image — only on large screens */}
      <Box
        component="img"
        src={ForChildren}
        alt="For Children"
        sx={{
          display: { xs: "none", lg: "block" },
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "520px",
          height: "auto",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ py: 3, position: "relative", zIndex: 1 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: "bold",
            fontFamily: '"Montserrat", sans-serif',
            color: "#B3FFC4",
            fontSize: { xs: "22px", sm: "26px", md: "30px" },
          }}
        >
          For Children
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 3, color: "#B3FFC4", lineHeight: 1.6,
            textAlign: "center", fontSize: { xs: "13px", md: "16px" },
          }}
        >
          Fun, Safe & Enriching Activities for Growing Minds
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} justifyContent="center">
          {childServices.map((service, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx} sx={{ display: "flex", justifyContent: "center" }}>
              <CareCard service={service} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ─── SOCIAL WELLNESS TRUST ────────────────────────────────────────────────────
function SocialWellnessTrust() {
  const trustPoints = [
    "Builds a sense of belonging and reduces loneliness",
    "Encourages self-expression and boosts confidence",
    "Strengthens family and community connections",
    "Supports cognitive and emotional health at all ages",
    "All activities facilitated by trained wellness coordinators and therapists",
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        backgroundColor: "#006699",
        color: "white",
        minHeight: { xs: "auto", md: "400px" },
        overflow: "hidden",
      }}
    >
      {/* Left: Text Section */}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",  
          alignItems: "center",    
          py: { xs: 4, md: 6 },
          px: 2,                  
          order: { xs: 2, md: 1 },
          textAlign: "center",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",justifyContent: "center", alignItems: "center",   
            mb: { xs: 2, md: 3 },
            fontSize: { xs: "20px", sm: "24px", md: "28px", lg: "32px" },
          }}
        >
          Why Mental & Social Wellness Matters with Ayuja
        </Typography>

        <List sx={{ p: 0 }}>
          {trustPoints.map((text, i) => (
            <ListItem key={i} sx={{ py: { xs: 0.8, md: 1.2 }, px: 0, alignItems: "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 40, mt: 0.3 }}>
                <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: { xs: "22px", md: "26px" } }} />
              </ListItemIcon>
              <ListItemText
                primary={text}
                primaryTypographyProps={{
                  fontSize: { xs: "14px", sm: "16px", md: "19px" },
                  fontWeight: "400",
                  color: "white",
                  lineHeight: 1.5,
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Right: Image — full height cover */}
      <Box
        sx={{
          // On mobile: normal image below text
          // On desktop: fixed width panel, image covers it fully
          width: { xs: "100%", md: "35%", lg: "32%" },
          minHeight: { xs: "260px", md: "100%" },
          order: { xs: 1, md: 2 },
          flexShrink: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          component="img"
          src={ParentTrust}
          alt="Parents and Child"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            display: "block",
            // On mobile keep natural aspect ratio
            minHeight: { xs: "260px", md: "400px" },
          }}
        />
      </Box>
    </Box>
  );
}

export default SocialWellness;